/**
 * Reise-copilot: deterministisk ruteplanlegging (ingen AI). Tar en ferdig TripSpec fra
 * copiloten og bygger en flerstopp-rute ved å GJENBRUKE eksisterende tjenester:
 *
 *   planTrip(tripSpec)
 *     ├── resolveCorridor(tripSpec)            (cachet per origin|dest|lodging — D7)
 *     │     ├── searchPlace(origin/dest)       ── geocode + nordisk validering (D4)
 *     │     ├── fetchDrivingRoute(from,to)      ── grov korridor
 *     │     └── fetchCorridorCandidates(...)    ── kandidat-stopp m/ koordinater (segmentert)
 *     ├── selectDayStops(candidates, coords)    ── ren geometri: projiser + bucket per dag
 *     └── fetchMultiStopRoute(from, stops, to)  ── OSRM multi-waypoint → RoutePlanAlternative
 *
 * INVARIANT: LLM-output valideres/klampes før den treffer geocode/ruting. Feil returneres
 * som et typet {ok:false}-resultat (ikke throw) så UI kan vise en klar melding.
 */

import { Coordinates, CampingSpot, TripSpec, TripLodging, TerrainPreference, MapBounds, DiscoverPOI } from '../types';
import { searchPlace, reverseArea } from './geocodingService';
import { fetchDrivingRoute } from './routingService';
import { fetchCorridorCandidates } from './routePlanningService';
import { fetchWikiForPath } from './wikiService';
import { fetchTourismPOIs, fetchCulturalHeritage } from './discoverService';
import { getDistance } from './geometryUtils';
import { detectCountry } from '../utils/countryDetection';
import i18n from '../i18n';

/**
 * Kjente nordiske byer → land. Overstyrer lands-affinitet når geokoderen returnerer
 * tvetydige treff (f.eks. «Malmø» = by i Sverige OG holme i Mandal). Uten dette ville
 * Bergen→Malmö feilaktig lande på holmen fordi origin-landet er NO.
 */
export const WELL_KNOWN_CITY_COUNTRY: Record<string, string> = {
  malmo: 'SE',
  stockholm: 'SE',
  goteborg: 'SE',
  gothenburg: 'SE',
  uppsala: 'SE',
  kobenhavn: 'DK',
  copenhagen: 'DK',
  aalborg: 'DK',
  odense: 'DK',
  oslo: 'NO',
  bergen: 'NO',
  trondheim: 'NO',
  stavanger: 'NO',
  tromso: 'NO',
};

/** Lokal fold (ø/ö/å/æ) — unngår avhengighet til geocodingService.foldNordic (mockes i tester). */
function foldCityKey(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/[øö]/g, 'o')
    .replace(/æ/g, 'ae')
    .replace(/å/g, 'a')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Landhint fra stedsnavn når det matcher en kjent by (første komma-del). */
export function wellKnownCityCountry(place?: string): string | null {
  if (!place) return null;
  const head = foldCityKey(place).split(',')[0].trim().replace(/\s+/g, '_');
  return WELL_KNOWN_CITY_COUNTRY[head] || null;
}

export interface RouteAttraction {
  name: string;
  latitude: number;
  longitude: number;
  url?: string;
  /** Kategori (fra «Oppdag»: museum/viewpoint/waterfall/castle/cultural_heritage …). */
  category?: string;
  /** Kilde: wikipedia | osm (turisme) | heritage (Riksantikvaren). */
  source?: 'wikipedia' | 'osm' | 'heritage';
  /** Bilde + kort beskrivelse (til kort-popupen på kartet). Fra Wikipedia-thumbnail /
   *  Oppdag-kildene når tilgjengelig. */
  imageUrl?: string;
  description?: string;
  /** Hvilke av reisefølgets interesser denne severdigheten treffer (for «passer: …»-merking). */
  matchedInterests?: string[];
}

// Norsk etikett per «Oppdag»-kategori — bakes inn i søketeksten så interesse-rangeringen
// (stemmet substring-match) treffer kategorien direkte, f.eks. «museer»→«museum»,
// «utsikt»→«utsiktspunkt», «kulturminner»→«kulturminne».
const CATEGORY_LABEL_NO: Record<string, string> = {
  museum: 'museum', viewpoint: 'utsiktspunkt utsikt panorama', attraction: 'attraksjon',
  waterfall: 'foss fossefall', monument: 'monument minnesmerke', memorial: 'minnesmerke',
  castle: 'borg slott festning', ruins: 'ruiner ruin', cultural_heritage: 'kulturminne kulturarv historie',
  beach: 'strand badeplass bading svømme sjøbad', shopping: 'shopping kjøpesenter butikker handle',
};

// Intern: attraksjon m/ søketekst (tittel+utdrag, lowercase) beholdt gjennom korridor-
// cachen så interesse-rangeringen kan kjøres på nytt uten nytt Wikipedia-kall når
// reisefølgets interesser endres (rangering skjer utenfor cachen i planTrip).
interface CorridorAttraction extends RouteAttraction { text: string; }

// Norske bøynings-endelser (lengst først) — strippes for enkel stemming så «museer»
// matcher «museum»/«museet» og «badeplasser» matcher «badeplass».
const NB_SUFFIXES = ['ene', 'ande', 'ende', 'er', 'ane', 'ar', 'en', 'et', 'a', 'e'];

/** Grov stemming: fjern lengste bøynings-endelse som lar minst 4 tegn stå igjen. Ren. */
function stem(word: string): string {
  for (const suf of NB_SUFFIXES) {
    if (word.length - suf.length >= 4 && word.endsWith(suf)) return word.slice(0, word.length - suf.length);
  }
  return word;
}

/**
 * Del en interesse i søkbare termer: stemmede ord ≥4 tegn (dropper småord). Stem gjør at
 * flertall/bestemt form i interessen treffer entall i teksten («museer»→«muse»→«museet»). Ren.
 */
function interestTerms(interest: string): string[] {
  const norm = interest.trim().toLowerCase();
  if (!norm) return [];
  const stems = norm.split(/[^a-zà-ÿ0-9]+/i).filter((w) => w.length >= 4).map(stem);
  return Array.from(new Set(stems));
}

/**
 * Ranger severdigheter etter hvor godt de matcher reisefølgets interesser. Ren funksjon.
 * Uten interesser: behold rekkefølgen (topp `limit`). Med interesser: de som treffer flest
 * interesser først (stabil sortering), annotert med matchedInterests. Fjerner intern `text`.
 */
export function rankAttractionsByInterest(
  attractions: CorridorAttraction[],
  interests: string[],
  limit = 15,
): RouteAttraction[] {
  const strip = (a: CorridorAttraction): RouteAttraction => ({ name: a.name, latitude: a.latitude, longitude: a.longitude, url: a.url, category: a.category, source: a.source, imageUrl: a.imageUrl, description: a.description });
  const cleanInterests = (interests || []).map((i) => (i || '').trim()).filter(Boolean);
  if (!cleanInterests.length) return attractions.slice(0, limit).map(strip);

  const scored = attractions.map((a, idx) => {
    const matched: string[] = [];
    for (const interest of cleanInterests) {
      const terms = interestTerms(interest);
      if (terms.some((t) => a.text.includes(t))) matched.push(interest);
    }
    return { a, idx, matched };
  });
  // Treff først (flest matchede interesser), ellers behold opprinnelig rekkefølge (stabil).
  scored.sort((x, y) => (y.matched.length - x.matched.length) || (x.idx - y.idx));
  return scored.slice(0, limit).map(({ a, matched }) => ({
    ...strip(a),
    ...(matched.length ? { matchedInterests: matched } : {}),
  }));
}

/**
 * Lesbart stopp-navn. Fricamp-plasser har ofte generiske/vei-navn («Veiende») som ikke
 * sier noe — for dem foretrekkes STED (kommune/fylke). Campingplasser har ekte navn →
 * vis navn (+ sted). Ren funksjon.
 */
export function stopLabel(spot: CampingSpot): string {
  const area = (spot.municipality || spot.county || '').trim();
  if (spot.spotType === 'commercial_campsite') {
    const nm = (spot.name || 'Campingplass').trim();
    return area && !nm.toLowerCase().includes(area.toLowerCase()) ? `${nm} (${area})` : nm;
  }
  return area || (spot.name || 'Fricamp').trim();
}

// Speiler tripCopilot.TRIP_MAX_STOPS (backend CJS kan ikke importeres i klient-TS).
// Konfigurerbar: sett VITE_COPILOT_MAX_STOPS høyere når egen OSRM er satt opp
// (demo-serveren tåler ikke mange waypoints i ett kall — D10). Default 12.
export const TRIP_MAX_STOPS = Number((import.meta as any).env?.VITE_COPILOT_MAX_STOPS) || 12;

// Harde tak per steg så total rutebygging holder seg godt under 20s (bruker-krav).
const CANDIDATE_TIMEOUT_MS = 6000;  // ytre tak på kandidat-søk (per-jobb er 5s inne)
const GEOCODE_TIMEOUT_MS = 5000;    // per sted-oppslag
const CORRIDOR_ROUTE_TIMEOUT_MS = 6000; // grov A→B-korridor (OSRM demo)
const ATTRACTION_TIMEOUT_MS = 5000; // Wikipedia-POI langs ruten (best-effort)

/** Sampl OSRM [lon,lat][] → Coordinates[] (~n punkter) for wiki-sti-søk. */
function sampleCoordPath(coords: [number, number][], n = 40): Coordinates[] {
  if (coords.length <= n) return coords.map(([lon, lat]) => ({ latitude: lat, longitude: lon }));
  const step = Math.max(1, Math.floor(coords.length / n));
  const out: Coordinates[] = [];
  for (let i = 0; i < coords.length; i += step) out.push({ latitude: coords[i][1], longitude: coords[i][0] });
  return out;
}

/** Promise med timeout-fallback. Rydder timeren så den ikke henger. Ren hjelper. */
function withTimeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<T>((resolve) => { timer = setTimeout(() => resolve(fallback), ms); });
  return Promise.race([p.catch(() => fallback), timeout]).finally(() => clearTimeout(timer));
}

// ─── Ren geometri (enhetstestbar, ingen nett) ───

/** Kumulativ avstand (meter) langs en OSRM [lon,lat]-polyline; samme lengde som coords. */
export function cumulativeMeters(coords: [number, number][]): number[] {
  const out = new Array<number>(coords.length);
  let acc = 0;
  for (let i = 0; i < coords.length; i++) {
    if (i > 0) {
      const [lon1, lat1] = coords[i - 1];
      const [lon2, lat2] = coords[i];
      acc += getDistance(lat1, lon1, lat2, lon2);
    }
    out[i] = acc;
  }
  return out;
}

/**
 * Projiser et punkt ned på polylinjen: returner kumulativ avstand-langs-rute (meter) til
 * det nærmeste rute-punktet. Samplet for ytelse (som isNearRoute).
 */
export function projectAlong(
  lat: number,
  lon: number,
  coords: [number, number][],
  cum: number[],
): { alongMeters: number; offMeters: number } {
  const step = Math.max(1, Math.floor(coords.length / 300));
  let best = Infinity;
  let bestIdx = 0;
  for (let i = 0; i < coords.length; i += step) {
    const [clon, clat] = coords[i];
    const d = getDistance(lat, lon, clat, clon);
    if (d < best) { best = d; bestIdx = i; }
  }
  return { alongMeters: cum[bestIdx] ?? 0, offMeters: best };
}

export interface DayStopResult {
  stops: CampingSpot[];
  requestedStops: number;  // hvor mange overnattingsstopp turen egentlig ba om
  hitMaxStops: boolean;    // ba om flere enn TRIP_MAX_STOPS → «del opp turen»
}

/**
 * Velg overnattingsstopp langs korridoren. Ren funksjon.
 *
 * Antall stopp = max(dager−1, ceil(totalKm/dailyMaxKm)−1), cappet til maxStops og til
 * antall kandidater. For hver daggrense velges nærmeste ubrukte kandidat (etter avstand
 * LANGS ruten, ikke luftlinje). Returnerer stopp sortert langs ruten.
 */
export function selectDayStops(
  candidates: CampingSpot[],
  coords: [number, number][],
  opts: { days?: number; dailyMaxKm?: number; maxStops?: number },
): DayStopResult {
  const maxStops = opts.maxStops ?? TRIP_MAX_STOPS;
  if (coords.length < 2) return { stops: [], requestedStops: 0, hitMaxStops: false };

  const cum = cumulativeMeters(coords);
  const totalMeters = cum[cum.length - 1] || 0;
  const totalKm = totalMeters / 1000;

  const nights = Math.max(0, (opts.days ?? 1) - 1);
  const byKm = opts.dailyMaxKm ? Math.max(0, Math.ceil(totalKm / opts.dailyMaxKm) - 1) : 0;
  const requestedStops = Math.max(nights, byKm);
  const hitMaxStops = requestedStops > maxStops;

  let target = Math.min(requestedStops, maxStops, candidates.length);
  if (target <= 0) return { stops: [], requestedStops, hitMaxStops };

  // Projiser kandidatene og sorter langs ruten.
  const projected = candidates
    .map((spot) => ({ spot, along: projectAlong(spot.location.latitude, spot.location.longitude, coords, cum).alongMeters }))
    .sort((a, b) => a.along - b.along);

  // Prioriter bekreftede/verifiserte plasser: de får redusert «avstands-straff» mot
  // daggrensen, så en bekreftet plass vinner over en litt nærmere ubekreftet (opptil ~1.7×).
  const isPreferred = (s: CampingSpot) => s.isVerified === true || s.spotType === 'confirmed';
  const used = new Set<number>();
  const chosen: { spot: CampingSpot; along: number }[] = [];
  for (let i = 1; i <= target; i++) {
    const boundary = (totalMeters * i) / (target + 1);
    let bestIdx = -1;
    let bestScore = Infinity;
    for (let j = 0; j < projected.length; j++) {
      if (used.has(j)) continue;
      const delta = Math.abs(projected[j].along - boundary);
      const score = delta * (isPreferred(projected[j].spot) ? 0.6 : 1);
      if (score < bestScore) { bestScore = score; bestIdx = j; }
    }
    if (bestIdx === -1) break;
    used.add(bestIdx);
    chosen.push(projected[bestIdx]);
  }

  chosen.sort((a, b) => a.along - b.along);
  return { stops: chosen.map((c) => c.spot), requestedStops, hitMaxStops };
}

export interface DayLeg {
  day: number;
  from: string;
  to: string;
  km: number;   // ca. kjørelengde denne dagen (fra korridor-projeksjon)
}

/**
 * Bygg en dag-for-dag-plan fra valgte stopp. Ren funksjon. Hver etappe mellom to
 * påfølgende waypoints (start → stopp1 → … → mål) er én kjøredag. km er ca. fra
 * kumulativ avstand langs korridoren (ikke omruting til hvert stopp — «ca.»).
 */
export function buildDayPlan(
  stops: CampingSpot[],
  coords: [number, number][],
  originName: string,
  destName: string,
): DayLeg[] {
  if (coords.length < 2) return [];
  const cum = cumulativeMeters(coords);
  const total = cum[cum.length - 1] || 0;

  const shortName = (n: string, fallback: string) => (n ? n.split(',')[0].trim() : fallback) || fallback;
  const points = [
    { name: shortName(originName, 'Start'), along: 0 },
    ...stops.map((s) => ({
      name: stopLabel(s),
      along: projectAlong(s.location.latitude, s.location.longitude, coords, cum).alongMeters,
    })),
    { name: shortName(destName, 'Mål'), along: total },
  ];

  const legs: DayLeg[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    legs.push({
      day: i + 1,
      from: points[i].name,
      to: points[i + 1].name,
      km: Math.max(0, Math.round((points[i + 1].along - points[i].along) / 1000)),
    });
  }
  return legs;
}

// ─── Orkestrering (nett) ───

export type PlanTripError =
  | 'incomplete_spec'
  | 'geocode_origin'
  | 'geocode_dest'
  | 'no_route';

export interface PlanTripSuccess {
  ok: true;
  stops: CampingSpot[];
  attractions: RouteAttraction[]; // Wikipedia-POI langs ruten
  dayPlan: DayLeg[];        // dag-for-dag etapper (Fase 2)
  requestedStops: number;
  hitMaxStops: boolean;
  emptyCorridor: boolean;   // ingen kandidater funnet → direkte rute
  resolvedOriginName: string;
  resolvedDestName: string;
  fromCoords: Coordinates;
  toCoords: Coordinates;
}
export interface PlanTripFailure {
  ok: false;
  error: PlanTripError;
  message: string;
}
export type PlanTripResult = PlanTripSuccess | PlanTripFailure;

interface ResolvedCorridor {
  fromCoords: Coordinates;
  toCoords: Coordinates;
  resolvedOriginName: string;
  resolvedDestName: string;
  corridorCoords: [number, number][];
  candidates: CampingSpot[];
  attractions: CorridorAttraction[];
}

// D7: cache korridor-kandidater per (origin|dest|lodging) så justeringer som kun endrer
// days/dailyMaxKm re-slicer lokalt uten ny geocode/Overpass-scan.
const corridorCache = new Map<string, ResolvedCorridor>();
const CORRIDOR_CACHE_MAX = 10;

function corridorKey(origin: string, destination: string, lodging: TripLodging): string {
  return `${origin.trim().toLowerCase()}|${destination.trim().toLowerCase()}|${lodging}`;
}

// Verdier fra AI-en som betyr «der brukeren er nå» → resolves til GPS, ikke geocode.
const CURRENT_LOCATION_TOKENS = new Set([
  'current_location', 'current location', 'min posisjon', 'min posisjon nå', 'nåværende posisjon',
  'her', 'her jeg er', 'der jeg er', 'my location', 'gps', 'posisjonen min',
]);
function isCurrentLocation(s?: string): boolean {
  return !!s && CURRENT_LOCATION_TOKENS.has(s.trim().toLowerCase());
}

function toGeocode(r: { lat: string | number; lon: string | number; display_name?: string } | undefined): { coords: Coordinates; name: string } | null {
  if (!r) return null;
  const latitude = parseFloat(String(r.lat));
  const longitude = parseFloat(String(r.lon));
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { coords: { latitude, longitude }, name: r.display_name || '' };
}

function firstGeocode(results: Awaited<ReturnType<typeof searchPlace>>): { coords: Coordinates; name: string } | null {
  return toGeocode(results && results[0]);
}

/** Landskode for et geocode-treff: eksplisitt countrycode, ellers utledet av koordinat. */
function resultCountry(r: { lat: string | number; lon: string | number; countrycode?: string }): string {
  if (r.countrycode) return String(r.countrycode).toUpperCase();
  return detectCountry(parseFloat(String(r.lat)), parseFloat(String(r.lon))) || '';
}

/**
 * Velg endepunkt med LANDS-AFFINITET: tvetydige stedsnavn («Malmø» = by i Sverige OG
 * holme i Mandal) skal foretrekke samme land som turens andre endepunkt. Toppresultatet
 * vinner som før, MED MINDRE det ligger i et annet land enn `preferCountry` og et av de
 * neste ~5 treffene ligger i riktig land — da vinner det. Ren funksjon (testbar).
 */
export function pickEndpointByCountry(
  results: Awaited<ReturnType<typeof searchPlace>>,
  preferCountry?: string | null,
): { coords: Coordinates; name: string } | null {
  if (!results || results.length === 0) return null;
  if (!preferCountry) return firstGeocode(results);
  const want = preferCountry.toUpperCase();
  if (resultCountry(results[0]) === want) return firstGeocode(results);
  const alt = results.slice(1, 6).find((r) => resultCountry(r) === want);
  return alt ? toGeocode(alt) : firstGeocode(results);
}

// Bygg noen bbox-er langs korridoren for «Oppdag»-oppslag (som er viewport-baserte).
function corridorBboxes(coords: [number, number][], count = 4, padDeg = 0.12): MapBounds[] {
  if (coords.length === 0) return [];
  const step = Math.max(1, Math.floor(coords.length / count));
  const boxes: MapBounds[] = [];
  for (let i = 0; i < coords.length && boxes.length < count; i += step) {
    const [lon, lat] = coords[i];
    boxes.push({ latMin: lat - padDeg, latMax: lat + padDeg, lonMin: lon - padDeg, lonMax: lon + padDeg });
  }
  return boxes;
}

// Er punktet innen ~maxKm fra et (samplet) korridorpunkt? (bbox-treff kan ligge langt av rute)
function nearCorridor(lat: number, lon: number, coords: [number, number][], maxKm = 15): boolean {
  const step = Math.max(1, Math.floor(coords.length / 100));
  for (let i = 0; i < coords.length; i += step) {
    if (getDistance(lat, lon, coords[i][1], coords[i][0]) <= maxKm * 1000) return true;
  }
  return false;
}

/**
 * Severdigheter fra «Oppdag» langs korridoren: OSM-turisme (utsikt/museum/foss/borg/…) +
 * Riksantikvaren kulturminner. Kategorisert → bakes inn i søketeksten så interesse-
 * rangeringen treffer presist. Best-effort/timeout-vaktet (Overpass kan 429-e; kulturminner
 * er en ren egen API). Filtrert nær ruten + dedup på navn.
 */
async function fetchCorridorDiscoverPOIs(coords: [number, number][]): Promise<CorridorAttraction[]> {
  const boxes = corridorBboxes(coords);
  const jobs = boxes.flatMap((b) => [
    withTimeout(fetchTourismPOIs(b), ATTRACTION_TIMEOUT_MS, [] as DiscoverPOI[]),
    withTimeout(fetchCulturalHeritage(b), ATTRACTION_TIMEOUT_MS, [] as DiscoverPOI[]),
  ]);
  const all = (await Promise.all(jobs)).flat();
  const seen = new Set<string>();
  const out: CorridorAttraction[] = [];
  for (const p of all) {
    if (!p || typeof p.lat !== 'number' || typeof p.lon !== 'number') continue;
    if (!p.name || p.name === 'Ukjent') continue;
    if (!nearCorridor(p.lat, p.lon, coords)) continue;
    const key = p.name.trim().toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const catLabel = CATEGORY_LABEL_NO[p.category] || p.category || '';
    out.push({
      name: p.name,
      latitude: p.lat,
      longitude: p.lon,
      url: p.url,
      category: p.category,
      source: p.type === 'heritage' ? 'heritage' : 'osm',
      imageUrl: p.imageUrl,
      description: p.description,
      text: `${p.name} ${p.description || ''} ${catLabel}`.toLowerCase(),
    });
  }
  return out;
}

/** Slå sammen severdigheter fra flere kilder, dedup på navn (behold første). Ren. */
function dedupeAttractions(list: CorridorAttraction[]): CorridorAttraction[] {
  const seen = new Set<string>();
  const out: CorridorAttraction[] = [];
  for (const a of list) {
    const key = a.name.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(a);
  }
  return out;
}

/** Geocode + korridor-rute + kandidater. Cachet. */
export async function resolveCorridor(
  tripSpec: TripSpec,
  opts: { terrainPrefs?: TerrainPreference[]; onStatus?: (msg: string) => void; currentPosition?: Coordinates | null; favoriteSpots?: CampingSpot[] } = {},
): Promise<ResolvedCorridor | PlanTripFailure> {
  const { origin, destination, lodging } = tripSpec;
  if (!origin || !destination || !lodging) {
    return { ok: false, error: 'incomplete_spec', message: 'Mangler start, mål eller overnattingstype.' };
  }

  // Forhåndsresolvede endepunkter: når brukeren har VALGT et eksakt sted (f.eks. «Endre»
  // i rutefeltet) ligger navn+koordinat i spec-en — bruk dem direkte i stedet for å
  // geokode teksten på nytt (som kunne gi et annet treff enn brukerens valg).
  const pinnedOrigin = tripSpec.resolvedOriginCoords && tripSpec.resolvedOriginName && origin === tripSpec.resolvedOriginName
    ? { coords: tripSpec.resolvedOriginCoords, name: tripSpec.resolvedOriginName } : null;
  const pinnedDest = tripSpec.resolvedDestCoords && tripSpec.resolvedDestName && destination === tripSpec.resolvedDestName
    ? { coords: tripSpec.resolvedDestCoords, name: tripSpec.resolvedDestName } : null;

  // Ikke cache når GPS er involvert (posisjonen endrer seg). Pinnede koordinater må inn
  // i nøkkelen — samme tekst kan ellers gjenbruke en korridor for et annet valgt punkt.
  const useCache = !isCurrentLocation(origin) && !isCurrentLocation(destination);
  const pinSuffix = `${pinnedOrigin ? `${pinnedOrigin.coords.latitude.toFixed(4)},${pinnedOrigin.coords.longitude.toFixed(4)}` : ''}|${pinnedDest ? `${pinnedDest.coords.latitude.toFixed(4)},${pinnedDest.coords.longitude.toFixed(4)}` : ''}`;
  const key = `${corridorKey(origin, destination, lodging)}|${pinSuffix}`;
  if (useCache) {
    const cached = corridorCache.get(key);
    if (cached) return cached;
  }

  // «current_location»/«min posisjon» → bruk GPS i stedet for å geokode strengen.
  const gps = opts.currentPosition;
  // Hent TREFFLISTER for begge endepunkter parallelt (ingen ekstra latens), velg deretter
  // med LANDS-AFFINITET: tvetydige navn («Malmø» = by i Sverige og holme i Mandal)
  // foretrekker samme land som det andre endepunktet. Ankeret er det sikreste: pinned/GPS
  // først, ellers starten — starter ruten i Sverige antas «Malmø» å være svensk (neste
  // treff i lista dekker ekte kryss-grense-turer).
  type Hit = { coords: Coordinates; name: string } | null;
  const fetchList = (place: string) => withTimeout(searchPlace(place, ['no', 'se', 'dk']), GEOCODE_TIMEOUT_MS, [] as Awaited<ReturnType<typeof searchPlace>>);
  const gpsHit: Hit = gps ? { coords: gps, name: i18n.t('copilot.current_location') } : null;
  const countryOf = (hit: Hit): string | null => (hit ? detectCountry(hit.coords.latitude, hit.coords.longitude) : null);

  opts.onStatus?.('Slår opp steder...');
  const originFixed: Hit = pinnedOrigin ?? (isCurrentLocation(origin) ? gpsHit : null);
  const destFixed: Hit = pinnedDest ?? (isCurrentLocation(destination) ? gpsHit : null);
  const [originList, destList] = await Promise.all([
    originFixed || (isCurrentLocation(origin)) ? Promise.resolve([]) : fetchList(origin),
    destFixed || (isCurrentLocation(destination)) ? Promise.resolve([]) : fetchList(destination),
  ]);
  // Anker-land: fast endepunkt > toppresultatet for start. For lister brukes geokoderens
  // countrycode (autoritativ) — detectCountry er en grov grensetilnærming (plasserer
  // f.eks. Göteborg på «norsk» side) og er kun fallback for pinned/GPS-punkter.
  const listCountry = (list: Awaited<ReturnType<typeof searchPlace>>): string | null =>
    (list && list[0] ? resultCountry(list[0]) || null : null);
  const anchorCountry = countryOf(originFixed) || countryOf(destFixed) || listCountry(originList);
  // Kjente byer (Malmö, Stockholm, …) vinner over origin-lands-affinitet — ellers blir
  // Bergen→«Malmø» holmen i Mandal fordi ankerlandet er NO.
  const originCountryPref = wellKnownCityCountry(origin) || countryOf(destFixed);
  const destCountryPref = wellKnownCityCountry(destination) || anchorCountry;
  const originHit: Hit = originFixed ?? pickEndpointByCountry(originList, originCountryPref);
  const destHit: Hit = destFixed ?? pickEndpointByCountry(destList, destCountryPref);
  if (!originHit) {
    const msg = isCurrentLocation(origin)
      ? 'Fant ikke posisjonen din — skru på GPS eller skriv startsted.'
      : `Fant ikke «${origin}». Kan du presisere?`;
    return { ok: false, error: 'geocode_origin', message: msg };
  }
  if (!destHit) {
    const msg = isCurrentLocation(destination)
      ? 'Fant ikke posisjonen din — skru på GPS eller skriv mål.'
      : `Fant ikke «${destination}». Kan du presisere?`;
    return { ok: false, error: 'geocode_dest', message: msg };
  }

  opts.onStatus?.('Beregner korridor...');
  const corridor = await withTimeout(fetchDrivingRoute(originHit.coords, destHit.coords), CORRIDOR_ROUTE_TIMEOUT_MS, null);
  if (!corridor) {
    return { ok: false, error: 'no_route', message: 'Fant ingen kjørerute mellom stedene (tidsavbrudd).' };
  }

  // Timeout-vakt: de offentlige Overpass-serverne rate-limiter/henger ofte. Blokker
  // ALDRI ruten på dem — etter CANDIDATE_TIMEOUT_MS gir vi opp og tegner uten stopp.
  const candidatesPromise = fetchCorridorCandidates(corridor.coordinates, {
    lodging,
    terrainPrefs: opts.terrainPrefs,
    onStatus: opts.onStatus,
    extraCandidates: opts.favoriteSpots,
  }).catch(() => [] as CampingSpot[]);
  const timeoutPromise = new Promise<CampingSpot[]>((resolve) => {
    setTimeout(() => resolve([]), CANDIDATE_TIMEOUT_MS);
  });
  // Attraksjoner langs ruten (Wikipedia) — best-effort, parallelt m/ kandidater. Behold
  // søketekst (tittel+utdrag) + flere kandidater (30) så interesse-rangeringen i planTrip
  // har noe å velge blant uten nytt kall når reisefølgets interesser endres.
  const attractionsPromise = withTimeout(
    fetchWikiForPath(sampleCoordPath(corridor.coordinates)).then((ws) =>
      ws.slice(0, 30).map((w): CorridorAttraction => ({
        name: w.title,
        latitude: w.location.latitude,
        longitude: w.location.longitude,
        url: w.url,
        source: 'wikipedia',
        imageUrl: w.thumbnail?.source,
        description: w.extract || undefined,
        text: `${w.title} ${w.extract || ''}`.toLowerCase(),
      })),
    ),
    ATTRACTION_TIMEOUT_MS, [] as CorridorAttraction[],
  );
  // «Oppdag»-severdigheter (OSM-turisme + Riksantikvaren) langs korridoren — kategorisert,
  // best-effort. Litt romsligere timeout enn wiki siden det er flere bbox-kall.
  const discoverPromise = withTimeout(fetchCorridorDiscoverPOIs(corridor.coordinates), ATTRACTION_TIMEOUT_MS + 1500, [] as CorridorAttraction[]);
  const [candidates, wikiAttractions, discoverPOIs] = await Promise.all([
    Promise.race([candidatesPromise, timeoutPromise]),
    attractionsPromise,
    discoverPromise,
  ]);
  // Slå sammen Wikipedia + Oppdag (kategoriserte kilder først → prioriteres ved cap),
  // dedup på navn, behold flere for interesse-rangeringen i planTrip.
  const attractions = dedupeAttractions([...discoverPOIs, ...wikiAttractions]).slice(0, 40);

  const resolved: ResolvedCorridor = {
    fromCoords: originHit.coords,
    toCoords: destHit.coords,
    resolvedOriginName: originHit.name,
    resolvedDestName: destHit.name,
    corridorCoords: corridor.coordinates,
    candidates,
    attractions,
  };

  if (useCache) {
    if (corridorCache.size >= CORRIDOR_CACHE_MAX) {
      const oldest = corridorCache.keys().next().value;
      if (oldest !== undefined) corridorCache.delete(oldest);
    }
    corridorCache.set(key, resolved);
  }
  return resolved;
}

/** Full planlegging fra en TripSpec. Returnerer en tegnbar RoutePlanAlternative eller feil. */
export async function planTrip(
  tripSpec: TripSpec,
  opts: { terrainPrefs?: TerrainPreference[]; onStatus?: (msg: string) => void; currentPosition?: Coordinates | null; favoriteSpots?: CampingSpot[]; excludeSpotIds?: string[]; maxStops?: number } = {},
): Promise<PlanTripResult> {
  const resolved = await resolveCorridor(tripSpec, opts);
  if ('ok' in resolved && resolved.ok === false) return resolved;
  const corridor = resolved as ResolvedCorridor;

  // Brukeren har fjernet stopp fra planen → ekskluder dem fra kandidatpoolen så
  // re-planleggingen velger ANDRE plasser langs ruten i stedet for å gjeninnsette samme.
  const excluded = new Set(opts.excludeSpotIds || []);
  const candidates = excluded.size ? corridor.candidates.filter((c) => !excluded.has(c.id)) : corridor.candidates;

  // opts.maxStops: brukeren har aktivt FJERNET stopp → ikke refyll plassen med en
  // nabo-plass (som ofte får samme kommune-etikett og ser identisk ut — «dødt ×»).
  const stopCap = Math.min(TRIP_MAX_STOPS, opts.maxStops ?? TRIP_MAX_STOPS);
  const { stops: rawStops, requestedStops, hitMaxStops } = selectDayStops(
    candidates,
    corridor.corridorCoords,
    { days: tripSpec.days, dailyMaxKm: tripSpec.dailyMaxKm, maxStops: stopCap },
  );

  // Fyll inn manglende sted (kommune) via reverse-geocode så stopp viser STED, ikke
  // veinavn — spesielt i Sverige der geonorge (norsk) ikke gir kommune. Kloner stopp
  // (muterer ikke delte spot-objekter). Best-effort m/ timeout så totaltiden holdes nede.
  const areas = await Promise.all(rawStops.map((s) =>
    (s.municipality || s.county) ? Promise.resolve('') : withTimeout(reverseArea(s.location), 5000, ''),
  ));
  const stops = rawStops.map((s, i) => (areas[i] ? { ...s, municipality: areas[i] } : s));

  // Selve tegningen skjer i App via routePlan-effekten (samme kodesti som
  // RoutePlannerModal) — copiloten er ikke en konkurrerende skriver av
  // routePlanAlternatives. Vi returnerer stopp + oppløste steder, App setter routePlan.
  const dayPlan = buildDayPlan(stops, corridor.corridorCoords, corridor.resolvedOriginName, corridor.resolvedDestName);

  // Ranger severdigheter etter reisefølgets interesser (utenfor korridor-cachen så
  // endrede interesser re-rangerer uten nytt Wikipedia-kall). De som treffer først.
  const attractions = rankAttractionsByInterest(corridor.attractions, tripSpec.interests || []);

  return {
    ok: true,
    stops,
    attractions,
    dayPlan,
    requestedStops,
    hitMaxStops,
    emptyCorridor: corridor.candidates.length === 0,
    resolvedOriginName: corridor.resolvedOriginName,
    resolvedDestName: corridor.resolvedDestName,
    fromCoords: corridor.fromCoords,
    toCoords: corridor.toCoords,
  };
}

/** Kun for test/vedlikehold: tøm korridor-cachen. */
export function _clearCorridorCache(): void {
  corridorCache.clear();
}
